import EmployeeByType from "@/components/Forms/EmployeeByType.tsx";
import { useFetchTeams } from "@/queries/useFetchTeam.ts";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { X } from "lucide-react";
import { RhfTextField } from "mui-rhf-integration";
import type React from "react";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-paper": {
        maxWidth: 1200,
        width: "100%",
        margin: theme.spacing(2),
    },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Title = styled(Typography)(() => ({
    fontSize: "1.25rem",
    fontWeight: 600,
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

const FormGrid = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
    },
}));

const FormField = styled(Box)(() => ({
    display: "flex",
    flexDirection: "column",
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
    fontSize: "0.75rem",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    textTransform: "uppercase",
    letterSpacing: "0.5px",
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
}));

const CancelButton = styled(Button)(({ theme }) => ({
    color: theme.palette.text.secondary,
    textTransform: "none",
    fontWeight: 500,
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
    textTransform: "none",
    fontWeight: 500,
    "&:hover": {
        backgroundColor: theme.palette.info.dark,
    },
}));

export interface CaseAssignmentFields {
    assignedTeam: string;
    assignedToAttorney: string;
    assignedToDisbursementSpecialist: string;
    assignedToIntakeSpecialist: string;
    assignedToParalegal: string;
    assignedToMedicalRecordsClerk: string;
}

interface CaseAssignmentModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (assignments: CaseAssignmentFields) => void;
    isSubmitting: boolean;
}

const CaseAssignmentModal: React.FC<CaseAssignmentModalProps> = ({
    open,
    onClose,
    onSubmit,
    isSubmitting,
}) => {
    const form = useForm<CaseAssignmentFields>({
        defaultValues: {
            assignedTeam: "",
            assignedToAttorney: "",
            assignedToDisbursementSpecialist: "",
            assignedToIntakeSpecialist: "",
            assignedToParalegal: "",
            assignedToMedicalRecordsClerk: "",
        },
    });

    const { data: teamsData, isLoading: teamsLoading } = useFetchTeams();

    const handleSubmit = useCallback(() => {
        const values = form.getValues();
        onSubmit(values);
    }, [form, onSubmit]);

    const handleClose = useCallback(() => {
        form.reset();
        onClose();
    }, [form, onClose]);

    const teamsWithBlank = [
        { value: "", label: "" },
        ...(teamsData?.map((team) => ({
            value: team.teamName || "",
            label: team.teamName || "",
        })) || []),
    ];

    return (
        <StyledDialog open={open} onClose={handleClose} maxWidth="lg">
            <StyledDialogTitle>
                <Title>Case Assignments</Title>
                <CloseButton onClick={handleClose} size="small" disabled={isSubmitting}>
                    <X size={20} />
                </CloseButton>
            </StyledDialogTitle>

            <StyledDialogContent>
                <FormProvider {...form}>
                    <FormGrid>
                        <FormField>
                            <FieldLabel>Team</FieldLabel>
                            {teamsLoading ? (
                                <CircularProgress size={20} />
                            ) : (
                                <RhfTextField
                                    select
                                    control={form.control}
                                    name="assignedTeam"
                                    sx={{
                                        width: "100%",
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    {teamsWithBlank.map((option) => (
                                        <MenuItem
                                            key={`team-${option.value}`}
                                            value={option.value}
                                            sx={{ minHeight: "32px !important" }}
                                        >
                                            {option.label || <em>Select Team</em>}
                                        </MenuItem>
                                    ))}
                                </RhfTextField>
                            )}
                        </FormField>

                        <FormField>
                            <FieldLabel>Attorney</FieldLabel>
                            <EmployeeByType label="" name="assignedToAttorney" type="Attorney" />
                        </FormField>

                        <FormField>
                            <FieldLabel>Disbursement Specialist</FieldLabel>
                            <EmployeeByType
                                label=""
                                name="assignedToDisbursementSpecialist"
                                type="DisbursementSpecialist"
                            />
                        </FormField>

                        <FormField>
                            <FieldLabel>Intake Specialist</FieldLabel>
                            <EmployeeByType
                                label=""
                                name="assignedToIntakeSpecialist"
                                type="IntakeSpecialist"
                            />
                        </FormField>

                        <FormField>
                            <FieldLabel>Case Manager</FieldLabel>
                            <EmployeeByType
                                label=""
                                name="assignedToParalegal"
                                type="CaseManager"
                            />
                        </FormField>

                        <FormField>
                            <FieldLabel>Medical Records Clerk</FieldLabel>
                            <EmployeeByType
                                label=""
                                name="assignedToMedicalRecordsClerk"
                                type="MedicalRecordsClerk"
                            />
                        </FormField>
                    </FormGrid>
                </FormProvider>
            </StyledDialogContent>

            <StyledDialogActions>
                <CancelButton onClick={handleClose} disabled={isSubmitting}>
                    Cancel
                </CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={isSubmitting} variant="contained">
                    {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Create Case"}
                </SubmitButton>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default CaseAssignmentModal;
